"use client";

import type { CSSProperties, MouseEvent, RefObject } from "react";
import Link from "next/link";
import { useSyncExternalStore } from "react";

import { HourglassMark } from "@/components/HourglassMark";

import { THEME_STORAGE_KEY } from "@/lib/documentTheme";

/** Kept for marketing assets / emails that reference the raster file path. */
export const BRAND_WORDMARK_IMAGE = "/brand/logo-full.png";

type Variant = "auth" | "nav" | "hero" | "dashboard" | "splash";

type ExpiryDeskWordmarkProps = {
  variant?: Variant;
  showTagline?: boolean;
  /** Overrides default tagline when `showTagline` is true. */
  taglineText?: string;
  /** Sand-flow animation on the hourglass “X”. */
  hourglassWithSand?: boolean;
  className?: string;
  style?: CSSProperties;
  /** When set, the wordmark is wrapped in a Next.js link (e.g. `/dashboard`). */
  href?: string;
  /** Applied to the link wrapper when `href` is set. */
  linkClassName?: string;
  /** Merged with the link’s click handler when `href` is set. */
  onLinkClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
  /** @deprecated No-op — wordmark is always vector for identical metrics in light and dark. */
  darkModeTurquoiseHourglass?: boolean;
  /** Optional anchor around the hourglass “X” for FLIP / launch measurement. */
  hourglassAnchorRef?: RefObject<HTMLSpanElement | null>;
};

function subscribeDocumentDark(onStoreChange: () => void) {
  if (typeof window === "undefined") return () => {};
  window.addEventListener("expirydesk-theme-change", onStoreChange);
  const onStorage = (e: StorageEvent) => {
    if (e.key === THEME_STORAGE_KEY || e.key === null) onStoreChange();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener("expirydesk-theme-change", onStoreChange);
    window.removeEventListener("storage", onStorage);
  };
}

function getDocumentDarkSnapshot(): boolean {
  if (typeof document === "undefined") return false;
  return document.documentElement.dataset.edTheme === "dark";
}

function getServerDarkSnapshot(): boolean {
  return false;
}

function useDocumentThemeDark() {
  return useSyncExternalStore(subscribeDocumentDark, getDocumentDarkSnapshot, getServerDarkSnapshot);
}

const hourglassSizeByVariant: Record<Variant, number> = {
  nav: 28,
  auth: 32,
  hero: 38,
  /** Same “X” scale as dashboard nav / landing chrome */
  splash: 28,
  dashboard: 30,
};

/**
 * Vector wordmark everywhere — same DOM in light and dark so toggling appearance does not resize
 * the header. Light: deep cyan “metal”; dark: ice chrome + jewel hourglass.
 */
function ComposedWordmark({
  variant,
  showTagline,
  taglineText,
  hourglassWithSand,
  isDark,
  hourglassAnchorRef,
}: {
  variant: Variant;
  showTagline: boolean;
  taglineText?: string;
  hourglassWithSand?: boolean;
  isDark: boolean;
  hourglassAnchorRef?: RefObject<HTMLSpanElement | null>;
}) {
  const hg = hourglassSizeByVariant[variant];
  const themeClass = isDark ? "ed-wordmark-composed--premium" : "ed-wordmark-composed--light";
  const tagline = taglineText ?? "TRACK • ALERT • PREVENT WASTE";
  const taglineClass =
    variant === "splash"
      ? "ed-wordmark-composed__tagline ed-wordmark-composed__tagline--splash"
      : `ed-wordmark-composed__tagline ${
          isDark ? "ed-wordmark-composed__tagline--premium" : "ed-wordmark-composed__tagline--light"
        }`;
  return (
    <div className={`ed-wordmark-composed ed-wordmark-composed--${variant} ${themeClass}`}>
      <span className="ed-wordmark-composed__row">
        <span className="ed-wordmark-composed__gloss">E</span>
        <span ref={hourglassAnchorRef ?? undefined} className="inline-flex shrink-0" data-ed-wordmark-x-anchor="">
          <HourglassMark
            size={hg}
            premiumDark={isDark}
            withSand={hourglassWithSand}
            className="ed-wordmark-composed__hg"
            title=""
          />
        </span>
        <span className="ed-wordmark-composed__gloss">piryDesk</span>
      </span>
      {showTagline ? <span className={taglineClass}>{tagline}</span> : null}
    </div>
  );
}

/**
 * Brand wordmark — always vector (no PNG) so light/dark toggles never change bar height or blur.
 */
export function ExpiryDeskWordmark({
  variant = "auth",
  showTagline = true,
  taglineText,
  hourglassWithSand = false,
  className,
  style,
  href,
  linkClassName,
  onLinkClick,
  darkModeTurquoiseHourglass: _unusedLegacy = false,
  hourglassAnchorRef,
}: ExpiryDeskWordmarkProps) {
  void _unusedLegacy;
  const isDark = useDocumentThemeDark();
  const inner = (
    <ComposedWordmark
      variant={variant}
      showTagline={showTagline}
      taglineText={taglineText}
      hourglassWithSand={hourglassWithSand}
      isDark={isDark}
      hourglassAnchorRef={hourglassAnchorRef}
    />
  );

  return (
    <div
      className={className}
      suppressHydrationWarning
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: variant === "nav" ? "flex-start" : "center",
        ...style,
      }}
    >
      {href ? (
        <Link
          href={href}
          className={linkClassName ?? "ed-desk-brand-link"}
          onClick={onLinkClick}
          style={{ lineHeight: 0, display: "block" }}
          aria-label="ExpiryDesk"
        >
          {inner}
        </Link>
      ) : (
        inner
      )}
    </div>
  );
}
